import {
  AnchorButton,
  Classes,
  H3,
  PanelProps,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import * as React from 'react';

import { TEST_IDS } from 'src/constants/testIds';
import { usePublishContext } from 'src/containers/PublishProjectDrawer/hooks';
import { ProductCard } from 'src/containers/PublishProjectDrawer/ProductCard';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import {
  ConnectionInfo,
  ModelInfo,
  RulesInfo,
  VirtualGraphMappingsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { connectionSelector } from 'src/selectors/connection';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import stardogExplorer from 'src/templates/images/stardog-explorer.svg';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import {
  ExplorerLinkProps,
  getExplorerLink,
} from 'src/utils/web/getExplorerLink';

export type OpenInExplorerPanelProps = {
  connectionInfo: ConnectionInfo;
  isPublishingRules: boolean;
  modelInfo: ModelInfo;
  ruleInfo: RulesInfo;
  vgMappingInfo?: VirtualGraphMappingsInfo;
};

export const OpenInExplorerPanel: React.VFC<
  PanelProps<OpenInExplorerPanelProps>
> = ({
  connectionInfo,
  isPublishingRules,
  modelInfo,
  ruleInfo,
  vgMappingInfo,
}) => {
  const dispatch = useReduxDispatch();
  const connection = useReduxSelector(connectionSelector);
  const dashboardUrl = connection?.dashboard || '';
  const { isPending } = usePublishContext();
  const hasReasoning = isPublishingRules && Boolean(ruleInfo);
  const linkProps: ExplorerLinkProps = {
    connectionDashboard: dashboardUrl,
    database: connectionInfo.databaseId,
    hasVirtualGraphs: Boolean(
      vgMappingInfo && Object.keys(vgMappingInfo).length
    ),
    model: modelInfo.modelName,
    hasReasoning,
  };
  const explorerLink = getExplorerLink(linkProps);

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false }));
  }, [dispatch]);
  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.openInExplorer.container}
      >
        <PublishProjectDrawerStepper active="none" />
        <div className="sd-drawer-publish-project__explorer-link">
          {isPending ? (
            <Spinner size={SpinnerSize.LARGE} />
          ) : (
            <>
              <H3>
                {copy.components.publishProjectDrawer.openInExplorer.success}
              </H3>
              <p>
                {
                  copy.components.publishProjectDrawer.openInExplorer
                    .successMessage
                }
              </p>

              <ProductCard
                altText={
                  copy.components.publishProjectDrawer.openInExplorer.card
                    .altText
                }
                description={
                  copy.components.publishProjectDrawer.openInExplorer.card
                    .description
                }
                image={stardogExplorer}
                label={
                  copy.components.publishProjectDrawer.openInExplorer.card.label
                }
                link={explorerLink}
              />
            </>
          )}
        </div>
      </div>
      <div
        className={Classes.DRAWER_FOOTER}
        data-testid={TEST_IDS.publishProjectDrawer.footer.container}
      >
        <div className="sd-drawer-footer-actions">
          <div />
          <AnchorButton
            disabled={isPending}
            loading={isPending}
            onClick={handleClose}
            outlined
            text={copy.components.publishProjectDrawer.openInExplorer.close}
          />
        </div>
      </div>
    </>
  );
};
